import React from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import InputBase from "@material-ui/core/InputBase";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import Chip from "@material-ui/core/Chip";
import Search from "@material-ui/icons/Search";
import FirstPage from "@material-ui/icons/FirstPage";
import LastPage from "@material-ui/icons/LastPage";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import ChevronRight from "@material-ui/icons/ChevronRight";
import AssignmentReturn from "@material-ui/icons/AssignmentReturn";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Table from "components/Table/Table.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardIcon from "components/Card/CardIcon.js";
import CardHeader from "components/Card/CardHeader.js";

import styles from "assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.js";

const useStyles = makeStyles(styles);

const useCustomStyles = makeStyles({
  filterRow: {
    display: "flex",
    gap: "10px",
    marginBottom: "10px",
    flexWrap: "wrap",
    alignItems: "center",
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #ccc",
    borderRadius: "4px",
    padding: "4px 10px",
    backgroundColor: "#fff",
    height: "34px",
  },
  selectBox: {
    border: "1px solid #ccc",
    borderRadius: "4px",
    padding: "2px 8px",
    fontSize: "14px",
    backgroundColor: "#fff",
    height: "34px",
  },
  dateInput: {
    border: "1px solid #ccc",
    borderRadius: "4px",
    padding: "5px 10px",
    fontSize: "14px",
    outline: "none",
    color: "#333",
    height: "34px",
    boxSizing: "border-box",
  },
  filterLabel: {
    fontSize: "13px",
    color: "#555",
    fontWeight: 500,
    whiteSpace: "nowrap",
  },
  filterGroup: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  activeFilters: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    marginBottom: "8px",
  },
  invoiceNum: {
    fontWeight: 600,
    color: "#0d47a1",
    fontSize: "13px",
  },
  originalNum: {
    fontWeight: 500,
    color: "#555",
    fontSize: "13px",
  },
});

const GRAPHQL_URL = (process.env.REACT_APP_API_URL || "http://localhost:5001") + "/graphql";

const QUERY = `
  query GetInvoices($filter: InvoiceFilterDto!) {
    invoicesWithPagination(filter: $filter) {
      data {
        id
        invoiceNumber
        invoiceDate
        returnNumber
        status
        subtotal
        discount
        total
        paid
        customer { id customerNumber name }
      }
      total page pageSize totalPages
    }
  }
`;

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const TIME_PRESETS = [
  { label: "All time", value: "all" },
  { label: "Today", value: "today" },
  { label: "This week", value: "week" },
  { label: "This month", value: "month" },
  { label: "Custom range", value: "custom" },
];

function getPresetDates(preset) {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  if (preset === "today") { const s = fmt(now); return { dateFrom: s, dateTo: s }; }
  if (preset === "week") {
    const mon = new Date(now);
    mon.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    return { dateFrom: fmt(mon), dateTo: fmt(sun) };
  }
  if (preset === "month") {
    return {
      dateFrom: fmt(new Date(now.getFullYear(), now.getMonth(), 1)),
      dateTo: fmt(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
    };
  }
  return { dateFrom: "", dateTo: "" };
}

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatNum(val) {
  if (val == null) return "0";
  return Number(val).toLocaleString("vi-VN");
}

const EMPTY_FILTER = {
  invoiceNumber: "", customerNumber: "", customerName: "", dateFrom: "", dateTo: "",
};

export default function ReturnedInvoicesPage() {
  const classes = useStyles();
  const custom = useCustomStyles();
  const history = useHistory();

  const [invoices, setInvoices] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(25);
  const [total, setTotal] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(1);
  const [pageInput, setPageInput] = React.useState("1");

  const [invoiceNumber, setInvoiceNumber] = React.useState("");
  const [customerNumber, setCustomerNumber] = React.useState("");
  const [customerName, setCustomerName] = React.useState("");
  const [timePeriod, setTimePeriod] = React.useState("all");
  const [dateFrom, setDateFrom] = React.useState("");
  const [dateTo, setDateTo] = React.useState("");

  const filterRef = React.useRef({ ...EMPTY_FILTER });
  const debounceRef = React.useRef(null);

  const fetchInvoices = React.useCallback((pg, ps, f) => {
    setLoading(true);
    setError("");
    const token = localStorage.getItem("token");
    fetch(GRAPHQL_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: token ? `Bearer ${token}` : "" },
      credentials: "include",
      body: JSON.stringify({
        query: QUERY,
        variables: {
          filter: {
            page: pg, pageSize: ps,
            isReturn: true,
            invoiceNumber: f.invoiceNumber || undefined,
            customerNumber: f.customerNumber || undefined,
            customerName: f.customerName || undefined,
            dateFrom: f.dateFrom || undefined,
            dateTo: f.dateTo || undefined,
          },
        },
      }),
    })
      .then((r) => r.json())
      .then(({ data, errors }) => {
        if (errors) { setError(errors[0].message); return; }
        const p = data.invoicesWithPagination;
        setInvoices(p.data);
        setTotal(p.total);
        setTotalPages(p.totalPages);
        setPage(p.page);
        setPageInput(String(p.page));
      })
      .catch(() => setError("Không thể kết nối máy chủ"))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    filterRef.current = { ...EMPTY_FILTER };
    fetchInvoices(1, pageSize, filterRef.current);
  }, []);

  const debounce = (updates) => {
    filterRef.current = { ...filterRef.current, ...updates };
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchInvoices(1, pageSize, filterRef.current);
    }, 400);
  };

  const immediate = (updates) => {
    const next = { ...filterRef.current, ...updates };
    filterRef.current = next;
    fetchInvoices(1, pageSize, next);
  };

  const handleTimePeriodChange = (e) => {
    const val = e.target.value;
    setTimePeriod(val);
    if (val !== "custom") {
      const { dateFrom: df, dateTo: dt } = getPresetDates(val);
      setDateFrom(df); setDateTo(dt);
      immediate({ dateFrom: df, dateTo: dt });
    }
  };
  const handleDateFromChange = (e) => { setDateFrom(e.target.value); immediate({ dateFrom: e.target.value }); };
  const handleDateToChange = (e) => { setDateTo(e.target.value); immediate({ dateTo: e.target.value }); };

  const clearFilter = (key) => {
    if (key === "date") {
      setDateFrom(""); setDateTo(""); setTimePeriod("all");
      immediate({ dateFrom: "", dateTo: "" });
    }
  };

  const handlePageSizeChange = (e) => { const ps = e.target.value; setPageSize(ps); fetchInvoices(1, ps, filterRef.current); };
  const goToPage = (pg) => { if (pg < 1 || pg > totalPages) return; fetchInvoices(pg, pageSize, filterRef.current); };
  const handlePageInputKey = (e) => { if (e.key === "Enter") { const pg = parseInt(pageInput, 10); if (!isNaN(pg)) goToPage(pg); } };

  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const tableData = invoices.map((inv) => [
    <span key={inv.id + "-n"} className={custom.invoiceNum}
      style={{ cursor: "pointer" }}
      onClick={() => history.push(`/admin/returns/${inv.id}`)}>
      {inv.invoiceNumber}
    </span>,
    formatDateTime(inv.invoiceDate),
    <span key={inv.id + "-orig"} className={custom.originalNum}>{inv.returnNumber || "—"}</span>,
    inv.customer?.customerNumber || "—",
    inv.customer?.name || "—",
    formatNum(inv.subtotal),
    formatNum(inv.discount),
    formatNum(inv.total),
    formatNum(inv.paid),
  ]);

  const activeChips = [];
  if (dateFrom || dateTo) {
    const lbl = dateFrom && dateTo ? `${dateFrom} → ${dateTo}` : dateFrom || dateTo;
    activeChips.push({ label: `Date: ${lbl}`, key: "date" });
  }

  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="info" icon>
            <CardIcon color="info"><AssignmentReturn /></CardIcon>
            <h4 className={classes.cardIconTitle}>Return Invoices</h4>
          </CardHeader>
          <CardBody>
            {error && <p style={{ color: "#f44336", textAlign: "center" }}>{error}</p>}

            {/* ── Row 1: text searches ── */}
            <div className={custom.filterRow}>
              <div className={custom.searchBox}>
                <Search style={{ color: "#aaa", marginRight: "6px", fontSize: "18px" }} />
                <InputBase
                  placeholder="Return number..."
                  value={invoiceNumber}
                  onChange={(e) => { setInvoiceNumber(e.target.value); debounce({ invoiceNumber: e.target.value }); }}
                  style={{ fontSize: "14px", minWidth: "150px" }}
                />
              </div>

              <div className={custom.searchBox}>
                <Search style={{ color: "#aaa", marginRight: "6px", fontSize: "18px" }} />
                <InputBase
                  placeholder="Customer number..."
                  value={customerNumber}
                  onChange={(e) => { setCustomerNumber(e.target.value); debounce({ customerNumber: e.target.value }); }}
                  style={{ fontSize: "14px", minWidth: "150px" }}
                />
              </div>

              <div className={custom.searchBox}>
                <Search style={{ color: "#aaa", marginRight: "6px", fontSize: "18px" }} />
                <InputBase
                  placeholder="Customer name..."
                  value={customerName}
                  onChange={(e) => { setCustomerName(e.target.value); debounce({ customerName: e.target.value }); }}
                  style={{ fontSize: "14px", minWidth: "160px" }}
                />
              </div>

              {/* Time */}
              <div className={custom.filterGroup}>
                <span className={custom.filterLabel}>Time:</span>
                <Select value={timePeriod} onChange={handleTimePeriodChange} disableUnderline className={custom.selectBox} style={{ minWidth: "130px" }}>
                  {TIME_PRESETS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </Select>
              </div>

              {timePeriod === "custom" && (
                <div className={custom.filterGroup}>
                  <input type="date" value={dateFrom} onChange={handleDateFromChange} className={custom.dateInput} title="From" />
                  <span style={{ color: "#aaa", fontSize: "13px" }}>→</span>
                  <input type="date" value={dateTo} onChange={handleDateToChange} className={custom.dateInput} title="To" />
                </div>
              )}
            </div>

            {/* Active filter chips */}
            {activeChips.length > 0 && (
              <div className={custom.activeFilters}>
                {activeChips.map((chip) => (
                  <Chip key={chip.key} label={chip.label} onDelete={() => clearFilter(chip.key)} size="small" style={{ fontSize: "12px" }} />
                ))}
              </div>
            )}

            {/* Table */}
            <Table
              tableHead={["Return number", "Time", "Original invoice", "Cust. code", "Customer name", "Sub-total", "Discount", "Total refund", "Paid"]}
              tableData={loading ? [] : tableData}
              customCellClasses={[classes.right, classes.right, classes.right, classes.right]}
              customClassesForCells={[5, 6, 7, 8]}
              customHeadCellClasses={[classes.right, classes.right, classes.right, classes.right]}
              customHeadClassesForCells={[5, 6, 7, 8]}
            />
            {loading && <p style={{ textAlign: "center", padding: "16px 0" }}>Loading...</p>}

            {/* Pagination */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 0", flexWrap: "wrap", fontSize: "14px" }}>
              <span>Show</span>
              <Select value={pageSize} onChange={handlePageSizeChange} disableUnderline
                style={{ fontSize: "14px", border: "1px solid #ccc", borderRadius: "4px", padding: "2px 8px" }}>
                {PAGE_SIZE_OPTIONS.map((opt) => <MenuItem key={opt} value={opt}>{opt} rows</MenuItem>)}
              </Select>

              <IconButton size="small" onClick={() => goToPage(1)} disabled={page <= 1}><FirstPage fontSize="small" /></IconButton>
              <IconButton size="small" onClick={() => goToPage(page - 1)} disabled={page <= 1}><ChevronLeft fontSize="small" /></IconButton>

              <input value={pageInput} onChange={(e) => setPageInput(e.target.value)} onKeyDown={handlePageInputKey}
                onBlur={() => { const pg = parseInt(pageInput, 10); if (!isNaN(pg)) goToPage(pg); else setPageInput(String(page)); }}
                style={{ width: "48px", textAlign: "center", border: "1px solid #ccc", borderRadius: "4px", padding: "4px 0", fontSize: "14px" }} />

              <IconButton size="small" onClick={() => goToPage(page + 1)} disabled={page >= totalPages}><ChevronRight fontSize="small" /></IconButton>
              <IconButton size="small" onClick={() => goToPage(totalPages)} disabled={page >= totalPages}><LastPage fontSize="small" /></IconButton>

              <span style={{ color: "#555" }}>{from} – {to} of {total.toLocaleString("vi-VN")} returns</span>
            </div>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
